import './payment.css'

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { HashLink } from 'react-router-hash-link'

import { usePackageFeesFromRequests } from '../../../api/package-req/hooks/usePackageFeesFromRequests'
import { useUpdateUserPackage } from '../../../api/user-package/mutations/useUpdateUserPackage'
import { binarySearchClosestValue } from '../../../helpers/binarySearchClosestNumber'
import calcWeight from '../../../helpers/CalculationFunction'
import { getBrandLogo } from '../../../helpers/getBrandLogo'
import { getPrice } from '../../../helpers/getPrice'
import { isValidIsraeliId } from '../../../helpers/israeliId'
import optionsIcon from '../../../imgs/add.png'
import applePayIcon from '../../../imgs/apple-pay.png'
import downIcon from '../../../imgs/arrow-down.png'
import upIcon from '../../../imgs/arrow-up.png'
import creditCardIcon from '../../../imgs/credit-card.png'
import googlePayIcon from '../../../imgs/google-pay.png'
import infoIcon from '../../../imgs/info.png'
import returnIcon from '../../../imgs/next.png'
import couponIcon from '../../../imgs/promo-code.png'
import taxIcon from '../../../imgs/tax.png'
import { QUERY_KEYS } from '../../../lib/queryKeys'
import { checkCoupon } from '../../../services/coupon'
import { createPaymentLink } from '../../../services/payments'
import { getPackageTaxes } from '../../../services/taxes'
import { setPackageForPayment } from '../../../store/packageSlice'
import { couponTypes } from '../../OfficeSide/Coupon/consts'
import ConfirmInsuranceModal from './ConfirmInsuranceModal'
// import ExtraItems from './ExtraItems'
import ShippingOptions from './ShippingOptions'

const TAX_FREE_THRESHOLD = 75
const ID_REQUIRED_THRESHOLD = 100

export default function Payment() {
  const { packageForPayment: item, EURToUSD, GBPToUSD, poalimRate, BOIrate } = useSelector(state => state.packages)
  const { loggedUser } = useSelector(state => state.user)
  const history = useHistory()
  const dispatch = useDispatch()
  const { mutateAsync: updatePackageMutation } = useUpdateUserPackage()

  // Get fees from package requests instead of package entity
  const { data: packageFees, isLoading: isLoadingPackageFees } = usePackageFeesFromRequests(item)
  //shipping options
  const [shippingOptionsPrices, setShippingOptionsPrices] = useState({})
  const [selectedShippingMethod, setSelectedShippingMethod] = useState('doorToDoor')

  //insurance
  const insuranceRef = useRef(null)
  const [insurancePrice, setInsurancePrice] = useState(0)
  const [isInsuranceAdded, setIsInsuranceAdded] = useState(false)
  const [isInsuranceModalDisplayed, setIsInsuranceModalDisplayed] = useState(false)
  const [isInsuranceInfoShow, setIsInsuranceInfoShow] = useState(false)

  //services
  const [isLoadingTaxes, setIsLoadingTaxes] = useState(false)
  const [taxesData, setTaxesData] = useState(null)
  const [isTaxesDetails, setIsTaxesDetails] = useState(false)
  const idRef = useRef(null)

  //coupon
  const couponInputRef = useRef(null)
  const [isLoadingCoupon, setIsLoadingCoupon] = useState(false)
  const [coupon, setCoupon] = useState(null)
  const [couponDiscountAmount, setCouponDiscountAmount] = useState(0)
  const [couponMsg, setCouponMsg] = useState({ value: '', isDanger: false })
  const [feesDiscount, setFeesDiscount] = useState(0)
  const queryClient = useQueryClient()
  const [finalPrice, setFinalPrice] = useState(0)
  const [isLoadingMeshulam, setIsLoadingMeshulam] = useState(false)
  const [errorModalShow, seterrorModalShow] = useState(false)
  const [showInfoModal, setshowInfoModal] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('creditCard')
  const [paymentUrl, setPaymentUrl] = useState(null)
  const [isSafari, setisSafari] = useState(false)
  const taxesDetailsRef = useRef(null)
  const [packageValue, setpackageValue] = useState(0)

  const [isItemsDetailsShown, setIsItemsDetailsShown] = useState(false)
  const itemsDetailsRef = useRef(null)

  const [errorMsg, seterrorMsg] = useState('')

  //Grow iFrame (meshulam)
  useEffect(() => {
    function iframeListener(result) {
      if (result.origin === 'https://secure.meshulam.co.il' || result.origin === 'https://sandbox.meshulam.co.il') {
        switch (result.data.action) {
          case 'close': {
            setPaymentUrl(null)
            break
          }
          case 'payment':
          case 'google_payment': {
            if (result.data.status == 1) {
              // success
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PACKAGES] })
              history.push('/payment-successful')
            }
            break
          }
          case 'failed_to_load_page': {
            setPaymentUrl(null)
            seterrorMsg('תקלה בשרת! אנא נסה שנית או פנה לשירות הלקוחות')
            seterrorModalShow(true)
            break
          }
        }
      }
    }
    window.addEventListener('message', result => iframeListener(result))
    return () => window.removeEventListener('message', iframeListener)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //detect browser
  useEffect(() => {
    // Get the user-agent string
    let userAgentString = navigator.userAgent
    // Detect Chrome
    let chromeAgent = userAgentString.indexOf('Chrome') > -1

    // Detect Safari
    let safariAgent = userAgentString.indexOf('Safari') > -1

    // Discard Safari since it also matches Chrome
    if (chromeAgent && safariAgent) safariAgent = false
    setisSafari(safariAgent)
  }, [])

  useEffect(() => {
    //get item. (2 options: props / localStorage)
    if (!item) {
      const itemFromLocalStorage = JSON.parse(localStorage.getItem('payment-page-package'))
      if (itemFromLocalStorage) dispatch(setPackageForPayment(itemFromLocalStorage))
      return
    }

    //When getting item from localStorage, its synchrony.
    //But the EURToUSD is asynchrony, so its 0 and the taxes not calculated.
    if (!EURToUSD) return
    const _packageValue = getValue()
    setpackageValue(_packageValue)

    // get Taxes
    if (_packageValue >= TAX_FREE_THRESHOLD) getTaxes()

    // get Shipping Prices
    const weight = calcWeight(item.width, item.length, item.height, item.weight)
    const country = item.warehouse
    const shippingPrices = binarySearchClosestValue(weight, country)
    setShippingOptionsPrices(shippingPrices)

    // calcInsurance
    if (_packageValue > 0) {
      const fixedRate = 1 // $
      const proportionalRate = 0.015 // %
      const shippingPrice = Number(shippingPrices['doorToDoor']) // avoid few cents changes...
      const insurancePrice = parseFloat(((_packageValue + shippingPrice) * proportionalRate + fixedRate).toFixed(2))
      setInsurancePrice(insurancePrice)
    }

    // calcTotalPrice
    calcTotalPrice(shippingPrices['doorToDoor'])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, EURToUSD])

  // calcTotalPrice Handler
  useEffect(() => {
    if (!shippingOptionsPrices[selectedShippingMethod]) return // avoid first render calc
    calcTotalPrice(shippingOptionsPrices[selectedShippingMethod])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShippingMethod, couponDiscountAmount, isInsuranceAdded, taxesData])

  // onCouponChange
  useEffect(() => {
    if (!coupon) {
      setCouponDiscountAmount(0)
      return
    }

    //check if the shipping method match the allowed shipping method
    if (coupon.shippingMethod && coupon.shippingMethod !== 'all' && coupon.shippingMethod !== selectedShippingMethod) {
      setCouponDiscountAmount(0)
      return
    }

    switch (coupon.type) {
      case couponTypes.PERCENTAGE:
        setCouponDiscountAmount(parseFloat((shippingOptionsPrices[selectedShippingMethod] * (coupon.value / 100)).toFixed(2)))
        break
      case couponTypes.FIXED:
        setCouponDiscountAmount(coupon.value)
        break
      case couponTypes.MktUniteFee:
        setCouponDiscountAmount(feesDiscount)
        break
      case couponTypes.WarehouseFees:
        setCouponDiscountAmount(feesDiscount)
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, selectedShippingMethod])

  const getValue = () => {
    if (item.valueInUSD) return item.valueInUSD
    else if (item.price) return getPrice(item, EURToUSD, GBPToUSD)
    return 0
  }

  const getTaxes = async () => {
    setIsLoadingTaxes(true)
    try {
      const data = await getPackageTaxes(item.id)
      setTaxesData(data)
    } catch (error) {
      console.log('getTaxes - error:', error)
      seterrorMsg('תקלה בשרת! אנא נסה שנית או פנה לשירות הלקוחות')
      seterrorModalShow(true)
      return
    }
    setIsLoadingTaxes(false)
  }

  const calcTotalPrice = shippingPrice => {
    const totalPrice =
      parseFloat(shippingPrice ?? 0) +
      parseFloat(packageFees?.uniteFee ?? item.uniteFee ?? 0) +
      parseFloat(item.delayChargePrice ?? 0) +
      parseFloat(item.taxFreeFeeUSD ?? 0) +
      parseFloat(packageFees?.shrinkRequestFeeUSD ?? item.shrinkRequestFeeUSD ?? 0) +
      parseFloat(packageFees?.photoRequestFeeUSD ?? item.photoRequestFeeUSD ?? 0) +
      parseFloat(isInsuranceAdded ? insurancePrice : 0) +
      (taxesData?.sum ?? 0) -
      parseFloat(couponDiscountAmount)
    setFinalPrice(Number(parseFloat(totalPrice).toFixed(2)))
  }

  //only for coupon min payment
  const getShippingPriceInILS = () => {
    const priceInUSD = parseFloat(shippingOptionsPrices[selectedShippingMethod])
    const totalPriceInILS = priceInUSD * 0.9 * 1.03 * poalimRate + priceInUSD * 0.1 * BOIrate

    return totalPriceInILS
  }

  const getShippingPriceString = priceType => {
    let price = shippingOptionsPrices[priceType]

    if (!price) return 'לא זמין'
    else return `${price}$`
  }

  const onSubmitCouponCode = async () => {
    const couponCode = couponInputRef.current.value

    setCoupon('')
    setCouponMsg({
      value: '',
      isDanger: false,
    })

    if (!selectedShippingMethod) {
      seterrorMsg('נא לבחור שיטת שילוח')
      return
    }

    if (!couponCode) {
      seterrorMsg('נא למלא קוד קופון')
      return
    }

    setIsLoadingCoupon(true)

    try {
      const res = await checkCoupon({
        code: couponCode,
        userId: loggedUser.Username,
        priceInILS: getShippingPriceInILS(),
        shippingMethod: selectedShippingMethod,
        contents: item.contents,
        brand: item.brand,
        uniteFee: packageFees?.uniteFee ?? 0,
        delayChargePrice: item.delayChargePrice ?? 0,
        warehouse: item.warehouse,
      })

      if (!res.errorCode && res.data) {
        setFeesDiscount(res.data.feesDiscount)
        setCoupon(res.data.coupon)

        setCouponMsg({
          value: 'הקופון הוזן בהצלחה',
          isDanger: false,
        })
        return
      }
      switch (res.errorCode) {
        case 1:
          setCouponMsg({
            value: 'הקופון לא נמצא',
            isDanger: true,
          })
          break
        case 2:
          setCouponMsg({
            value: 'קופון אזל :(',
            isDanger: true,
          })
          break
        case 3:
          setCouponMsg({
            value: 'קופון פג תוקף :(',
            isDanger: true,
          })
          break
        case 4:
          setCouponMsg({
            value: 'קופון לא זמין :(',
            isDanger: true,
          })
          break
        case 5:
          setCouponMsg({
            value: 'השתמשת כבר בקופון זה',
            isDanger: true,
          })
          break
        case 6:
          setCouponMsg({
            value: 'השתמשת כבר בקופון זה',
            isDanger: true,
          })
          break
        case 7:
          setCouponMsg({
            value: `לקופון זה יש מגבלת מינימום של ${res.response.minPayment} ש"ח`,
            isDanger: true,
          })
          break
        case 8:
          setCouponMsg({
            value: `קופון זה לא תקף לסוג המשלוח שבחרת`,
            isDanger: true,
          })
          break
        case 9:
          setCouponMsg({
            value: `קופון זה לא תקף לקטגורית מוצרים זו`,
            isDanger: true,
          })
          break
        case 10:
          setCouponMsg({
            value: `קופון זה לא תקף למדינה זו`,
            isDanger: true,
          })
          break
        default:
          setCouponMsg({
            value: 'לא ניתן לטפל בבקשה כעת',
            isDanger: true,
          })
      }
    } catch (err) {
      console.log('onSubmitCouponCode - err', err)
      seterrorMsg('תקלה בשרת! אנא נסה שנית או פנה לשירות הלקוחות')
      seterrorModalShow(true)
    } finally {
      setIsLoadingCoupon(false)
    }
  }

  const addIsraeliIdToPackage = async () => {
    if (!idRef.current) return

    await updatePackageMutation({
      packageId: item.id,
      updateParam: {
        costumerIsraeliId: idRef.current.value,
      },
    })
  }

  const onPaymentClick = () => {
    if (idRef.current && !isValidIsraeliId(idRef.current.value)) {
      seterrorMsg('מספר תעודת הזהות לא תקין.')
      seterrorModalShow(true)
      idRef.current.focus()
      return
    }
    if (!isInsuranceAdded) {
      setIsInsuranceModalDisplayed(true)
      return
    }
    goToPaymentService()
  }

  const goToPaymentService = async () => {
    setIsLoadingMeshulam(true)
    addIsraeliIdToPackage()

    const paymentFormData = {
      email: loggedUser.UserAttributes.email,
      fullName: loggedUser.UserAttributes.name + ' ' + loggedUser.UserAttributes.family_name,
      phone: loggedUser.UserAttributes.phone_number.split('+972')[1],
      pid: item.id,
      sm: selectedShippingMethod,
      contentVal: packageValue.toString(),
      couponDiscount: couponDiscountAmount,
      couponCode: coupon?.code ?? '',
      contents: item.contents,
      paymentMethod,
      country: item.warehouse,
    }
    if (isInsuranceAdded) paymentFormData.insurance = insurancePrice
    if (taxesData?.sum) paymentFormData.taxesToPay = taxesData.sum * poalimRate

    try {
      const paymentLink = await createPaymentLink(paymentFormData)

      //apple wants new page
      if (paymentMethod == 'apple') window.location.href = paymentLink.data.data
      else setPaymentUrl(paymentLink.data.data)
    } catch (error) {
      console.log('goToPaymentService - error:', error)
      seterrorMsg('תקלה בשרת! אנא נסה שנית או פנה לשירות הלקוחות')
      seterrorModalShow(true)
    }
    setIsLoadingMeshulam(false)
  }

  if (!item || !EURToUSD) return <></>

  return (
    <>
      {isInsuranceModalDisplayed && (
        <ConfirmInsuranceModal
          onSkipInsurance={() => {
            setIsInsuranceModalDisplayed(false)
            goToPaymentService()
          }}
          onAddInsurance={() => {
            setIsInsuranceModalDisplayed(false)
          }}
        />
      )}

      <Modal show={errorModalShow} onHide={() => seterrorModalShow(false)}>
        <Modal.Header style={{ textAlign: 'right' }}>
          <div style={{ width: '100%' }}>
            <Modal.Title style={{ direction: 'rtl' }}>שים לב!</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body style={{ direction: 'rtl' }}> {errorMsg} </Modal.Body>
        <Modal.Footer style={{ direction: 'rtl' }}>
          <Button variant='danger' onClick={() => seterrorModalShow(false)}>
            סגור
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={isInsuranceInfoShow}
        onHide={() => setIsInsuranceInfoShow(false)}
        style={{ textAlign: 'center', direction: 'rtl' }}
        centered={true}
      >
        <Modal.Body>
          <span className='service-option-description'>
            למען הסר ספק, שיפטנבול לוקחת אחריות מלאה על חבילות שאבדו מהרגע שנשלחו מהמרכז הלוגיסטי עד שהן מגיעות אליכם באופן אוטומטי.
            <br />
            <br />
            <span style={{ fontWeight: 'bold' }}>הביטוח יאפשר לכם:</span>
            <br />
            <div style={{ textAlign: 'right' }}>
              - לבטח את סה״כ העסקה: עלות המוצרים ונזקים שנגרמו לתכולת החבילה מהצד של הספק + דמי המשלוח.
              <br />
              - החזר מהיר של עד 7 ימי עסקים.
              <br />- החזר כספי לחשבון במקום זיכוי לשימוש חוזר.
            </div>
            <br />
            <span style={{ fontWeight: 'bold' }}>שימו לב!</span>
            <br />
            <div style={{ textAlign: 'right' }}>
              - לא ניתן לבטח פריטים שבירים. מוצרים אלו נשלחים באריזתן המקורית ואנו מוסיפים רק מעטפת הגנה חיצונית.
            </div>
            <br />
            *בכפוף לתנאי השימוש באתר שלנו.
            <br />
            רוצים לדעת את כל הפרטים?
            <a href='https://www.shiptanbul.com/knowledge-center/faqs/insurance' target='_blank' rel='noreferrer'>
              {' '}
              לחצו כאן.
            </a>
          </span>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'center' }}>
          <Button variant='danger' onClick={() => setIsInsuranceInfoShow(false)}>
            סגור
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showInfoModal} onHide={() => setshowInfoModal(false)} style={{ textAlign: 'center', direction: 'rtl' }} centered={true}>
        <Modal.Header>
          <div style={{ width: '100%' }}>
            <Modal.Title>שים לב!❤️</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div>וודאו שכל הפריטים שהזמנתם אכן הגיעו.</div>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'center' }}>
          <Button
            variant='danger'
            onClick={() => {
              setshowInfoModal(false)
            }}
          >
            אישור
          </Button>
        </Modal.Footer>
      </Modal>
      {!paymentUrl ? (
        <div className='payment-return-cont' onClick={() => history.push('/personal-info/myPackages')}>
          <div>חזרה לחבילות שלי</div>
          <img src={returnIcon} className='payment-return-img' />
        </div>
      ) : (
        <div className='payment-return-cont' onClick={() => setPaymentUrl(null)}>
          <img src={returnIcon} className='payment-return-img' />
        </div>
      )}

      <h2 className='page-title'>תשלום</h2>
      {!paymentUrl ? (
        <div className='payment-container'>
          <div className='payment-cont-top'>
            <div className='payment-cont-top-bcg'>
              <ShippingOptions
                pricesObj={shippingOptionsPrices}
                setSelectedShippingMethod={setSelectedShippingMethod}
                getItemPrice={getShippingPriceString}
              />

              <div className='services-container'>
                <div className='payment-title-cont'>
                  <img src={optionsIcon} className='payment-title-img' />
                  <h4 className='page-subtitle'>אפשרויות נוספות</h4>
                </div>

                <div className='service-option-container'>
                  <div className='payment-checkbox'>
                    <div className='payment-checkbox-cont glow'>
                      <input
                        type='checkbox'
                        disabled={!item || packageValue === 0}
                        ref={insuranceRef}
                        id='insuranceCheckbox'
                        checked={isInsuranceAdded}
                        onChange={ev => setIsInsuranceAdded(ev.target.checked)}
                      />
                    </div>
                  </div>
                  <span className='service-option-title' style={{ paddingTop: '3px' }}>
                    ביטוח 360
                    {insurancePrice > 0 && <span className='service-option-price'>({insurancePrice.toFixed(2)}$)</span>}
                    <span className='service-option-info' onClick={() => setIsInsuranceInfoShow(!isInsuranceInfoShow)}>
                      {' '}
                      - מה כולל הביטוח?{' '}
                    </span>
                    <br />
                  </span>
                  <span></span>
                  <span> הידעת? כ-90% מהלקוחות שלנו בוחרים לרכוש ביטוח ולהיות הכי רגועים בעולם 😇</span>

                  {packageValue === 0 && (
                    <span className='service-option-description'>
                      אם ברצונך לבטח את החבילה אנא צור קשר עם <HashLink to='/about#contact-us'>שירות הלקוחות</HashLink>
                    </span>
                  )}
                </div>
                <br />
                {packageValue > 0 && (
                  <>
                    <div className='payment-title-cont'>
                      <img src={taxIcon} className='payment-title-img' />
                      <h4 className='page-subtitle'>מיסים</h4>
                    </div>
                    {packageValue < TAX_FREE_THRESHOLD && <div>איזה כיף! החבילה בשווי נמוך מ-75$ ולכן פטורה ממיסים.</div>}

                    {packageValue > TAX_FREE_THRESHOLD && (
                      <div className='service-tax-container'>
                        <span className='service-option-title'>
                          תשלום מיסים מראש
                          {isLoadingTaxes ? (
                            <Spinner animation='border' variant='dark' size='sm' style={{ margin: '0 0.5rem' }} />
                          ) : (
                            <span className='service-option-price'>({taxesData?.sum.toFixed(2)}$)</span>
                          )}
                        </span>
                        <span className='service-option-description'>שווי החבילה מעל {TAX_FREE_THRESHOLD}$ ולכן היא חייבת במיסים.</span>

                        {packageValue > ID_REQUIRED_THRESHOLD && (
                          <div className='service-tax-id'>
                            <span className='service-option-description'>
                              מכיוון ששווי החבילה מעל {ID_REQUIRED_THRESHOLD}$ יש צורך גם בתעודת זהות:
                            </span>
                            <input ref={idRef} className='coupon-input' type='text' name='id' placeholder='ת.ז.' />
                          </div>
                        )}

                        <span className='payment-success-message'>
                          * טיפ לפעם הבאה: לפעמים זול יותר לפצל הזמנה גדולה לכמה הזמנות מהספק כדי לחסוך בעלויות המיסים 😎
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className='payment-coupon-container'>
                  <div className='payment-coupon-top'>
                    <img src={couponIcon} style={{ height: '38px', marginLeft: '7px' }} />
                    <div className='payment-coupon'>
                      <input ref={couponInputRef} className='coupon-input' type='text' name='couponCode' placeholder='קוד קופון' />
                    </div>
                    <div className='price-button'>
                      <Button onClick={onSubmitCouponCode} className='check-coupon-button'>
                        {isLoadingCoupon ? <Spinner animation='border' variant='secondary' size='sm' /> : 'שלח'}
                      </Button>
                    </div>
                  </div>

                  <div
                    className='payment-coupon-msg'
                    style={{
                      color: couponMsg.isDanger ? 'red' : '#00aeef',
                    }}
                  >
                    {couponMsg.value}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='selected-package-cont'>
            <div className='selected-package'>
              {/* <h4 className="page-subtitle">החבילה שלך</h4> */}
              <div className='selected-package-brand-cont'>
                <img src={getBrandLogo(item.brand)} alt={item.brand} width='100px' />
                <span>{item.brand}</span>
              </div>
              <div className='selected-package-row'>
                <span className='bold-span'>הגיעה למחסן בתאריך: </span> <span>{item.dateArrived.split('-').reverse().join('.')}</span>
              </div>
              <div className='selected-package-row'>
                <span className='bold-span'>שווי: </span> <span>{packageValue > 0 ? `${packageValue}$` : `-`}</span>
              </div>
              <div className='selected-package-row'>
                <span className='bold-span'>
                  פריטים:
                  <img
                    onClick={() => {
                      setIsItemsDetailsShown(!isItemsDetailsShown)
                      if (itemsDetailsRef.current) {
                        itemsDetailsRef.current.classList.toggle('payment-package-content-active')
                      }
                    }}
                    src={isItemsDetailsShown ? upIcon : downIcon}
                    alt='פירוט'
                    height='12px'
                    style={{ margin: '0 0.25rem', cursor: 'pointer' }}
                  />
                </span>
                <span>
                  <img
                    src={infoIcon}
                    alt='info'
                    onClick={() => setshowInfoModal(true)}
                    style={{ width: '16px', marginRight: '2px', verticalAlign: 'middle', cursor: 'pointer' }}
                  />
                </span>
              </div>

              <div className='payment-package-content' style={{ direction: 'ltr' }} ref={itemsDetailsRef}>
                <span>
                  {item?.contents?.map((c, i) => {
                    return (
                      <span key={i}>
                        {i > 0 && ', '}
                        {c.quantity} X {c.productCategory}
                      </span>
                    )
                  })}
                </span>
              </div>

              <div className='selected-package-row'>
                <span className='bold-span'>גודל ומשקל: </span>{' '}
                <span>
                  {item.length}x{item.height}x{item.width} , {item.weight}KG
                </span>
              </div>
              <div className='selected-package-row'>
                <span className='bold-span'>מספר מעקב מקומי: </span> <span>{item.trackingNumberInTurkey}</span>
              </div>
            </div>
          </div>

          <div className='payment-method-cont'>
            <h4 className='page-subtitle' style={{ textAlign: 'center', marginBottom: '10px' }}>
              אפשרויות תשלום
            </h4>
            <div className='payment-method-choice'>
              <input
                type='radio'
                id='pm-credit'
                name='payment-method'
                value='creditCard'
                checked={paymentMethod === 'creditCard'}
                onChange={() => setPaymentMethod('creditCard')}
              />
              <img src={creditCardIcon} alt='credit card' />
              <div> - כרטיס אשראי</div>
            </div>

            <div className='payment-method-choice'>
              <input
                type='radio'
                id='pm-google'
                name='payment-method'
                value='google'
                checked={paymentMethod === 'google'}
                onChange={() => setPaymentMethod('google')}
              />
              <img src={googlePayIcon} alt='Google Pay' />
              <div> - Google Pay</div>
            </div>

            {isSafari && (
              <div className='payment-method-choice'>
                <input
                  type='radio'
                  id='pm-apple'
                  name='payment-method'
                  value='apple'
                  checked={paymentMethod === 'apple'}
                  onChange={() => setPaymentMethod('apple')}
                />
                <img src={applePayIcon} alt='Apple Pay' />
                <div> - Apple Pay</div>
              </div>
            )}
          </div>

          <div className='payment-sum-cont'>
            <div className='payment-sum'>
              <h4 className='page-subtitle' style={{ textAlign: 'center' }}>
                סיכום
              </h4>
              <div className='payment-sum-row'>
                <span>משלוח:</span> <span>{getShippingPriceString(selectedShippingMethod)}</span>
              </div>
              {item.delayChargePrice > 0 && (
                <div className='payment-sum-row'>
                  <span>אחסון:</span> <span>{item.delayChargePrice}$</span>
                </div>
              )}
              <div className='payment-sum-row'>
                <span>ביטוח:</span> <span>{isInsuranceAdded ? insurancePrice : 0}$</span>
              </div>
              {item.taxFreeFeeUSD > 0 && (
                <div className='payment-sum-row'>
                  <span>עלות העברה מדלוואר:</span> <span>{item.taxFreeFeeUSD}$</span>
                </div>
              )}
              {packageFees?.uniteFee > 0 && (
                <div className='payment-sum-row'>
                  <span>תוספת בגין איחוד חבילות:</span> <span>{packageFees.uniteFee}$</span>
                </div>
              )}
              {packageFees?.photoRequestFeeUSD > 0 && (
                <div className='payment-sum-row'>
                  <span>עמלת צילום חבילה:</span> <span>{packageFees.photoRequestFeeUSD}$</span>
                </div>
              )}
              {packageFees?.shrinkRequestFeeUSD > 0 && (
                <div className='payment-sum-row'>
                  <span>עמלת צמצום חבילה:</span> <span>{packageFees.shrinkRequestFeeUSD}$</span>
                </div>
              )}
              <div className='payment-sum-row'>
                <span>
                  תשלום מיסים מראש:
                  {taxesData?.sum > 0 && (
                    <img
                      onClick={() => {
                        setIsTaxesDetails(!isTaxesDetails)
                        window.scrollBy(0, isTaxesDetails ? 0 : 100) // do-not-scroll effect
                        taxesDetailsRef.current.classList.toggle('payment-taxes-info-active')
                      }}
                      src={isTaxesDetails ? upIcon : downIcon}
                      alt='פירוט'
                      height='12px'
                      style={{ margin: '0 0.25rem', cursor: 'pointer' }}
                    />
                  )}
                </span>
                {isLoadingTaxes ? (
                  <Spinner animation='border' variant='dark' size='sm' />
                ) : (
                  <span>{taxesData?.sum ? taxesData.sum.toFixed(2) : 0}$</span>
                )}
              </div>
              {/* {isTaxesDetails && ( */}
              <div className='payment-taxes-info' ref={taxesDetailsRef}>
                <div className='payment-taxes-row'>
                  <span>מעמ:</span> <span>{taxesData?.details?.vat}$</span>
                </div>
                <div className='payment-taxes-row'>
                  <span>אגרות:</span> <span>{taxesData?.details?.externalFees}$</span>
                </div>
                <div className='payment-taxes-row'>
                  <span>עמלת שחרור:</span> <span>{taxesData?.details?.shiptanbulFee}$</span>
                </div>
              </div>
              {/* )} */}
              {coupon && (
                <div className='payment-sum-row' style={{ color: '#00aeef', fontWeight: 'bold' }}>
                  <span>קופון {coupon.code} :</span> <span> - {couponDiscountAmount}$</span>
                </div>
              )}

              <div className='payment-sum-row payment-sum-last'>
                <span>סה"כ:</span> <span>{finalPrice}$</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className='payment-iframe-cont'>
          <div className='payment-iframe-cont1'>
            <iframe
              src={paymentUrl}
              height='625'
              width='100%'
              title='Grow payment iframe'
              style={{ border: 'none' }}
              allow='payment'
            ></iframe>
          </div>
        </div>
      )}

      {!paymentUrl && (
        <div className='sticky-payment-cont'>
          {isLoadingMeshulam ? (
            <div className='spinner-cont'>
              <Spinner animation='border' variant='primary'></Spinner>
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 'bold' }}>
                <span>סה"כ: </span>
                <span>{finalPrice}$</span>
              </div>
              <Button
                onClick={onPaymentClick}
                disabled={
                  (selectedShippingMethod !== 'doorToDoor' && selectedShippingMethod !== 'pickUpPoint') ||
                  selectedShippingMethod === '' ||
                  isLoadingTaxes ||
                  isLoadingPackageFees
                }
                variant='primary'
                className='width-for-mobile'
              >
                תשלום
              </Button>
            </>
          )}{' '}
        </div>
      )}
    </>
  )
}
